import { create } from "zustand";
import {
  getOrganizationDetails,
  updateOrganizationDetails,
  getLeadershipPositions,
  createLeadershipPosition,
  getLeadershipHierarchy,
  getLeadershipTree,
  assignLeadershipPosition,
  updateLeadershipAssignment,
  removeLeadershipAssignment,
  getOrganizationStats,
  getAvailableEmployees,
  getWorkingHoursSettings,
  updateWorkingHoursSettings,
  triggerAttendanceNotifications,
  getNotificationStatus,
  getAllTeams,
  getTeamById,
  createTeam,
  updateTeam,
  deleteTeam,
  getAvailableManagers,
  getAllEmployeesForManager,
  OrganizationDetails,
  LeadershipPosition,
  LeadershipAssignment,
  OrganizationStats,
  CreateLeadershipAssignment,
  AvailableEmployee,
  WorkingHoursSettings,
  NotificationStatus,
  Team,
  CreateTeam,
  AvailableManager
} from "@/services/OrganizationSetupService";
import { registerStoreReset } from "@/store/storeResetUtils";

interface OrganizationSetupStore {
  // State
  organizationDetails: OrganizationDetails | null;
  leadershipPositions: LeadershipPosition[];
  leadershipHierarchy: LeadershipAssignment[];
  leadershipTree: LeadershipAssignment[];
  stats: OrganizationStats | null;
  availableEmployees: AvailableEmployee[];
  workingHoursSettings: WorkingHoursSettings | null;
  notificationStatus: NotificationStatus | null;
  teams: Team[];
  selectedTeam: Team | null;
  availableManagers: AvailableManager[];
  allEmployeesForManager: AvailableManager[];
  teamsLoading: boolean;
  loading: boolean;
  detailsLoading: boolean;
  hierarchyLoading: boolean;
  workingHoursLoading: boolean;
  triggerLoading: boolean;
  error: string | null;
  successMessage: string | null;

  // Actions
  fetchOrganizationDetails: () => Promise<void>;
  saveOrganizationDetails: (data: Partial<OrganizationDetails>) => Promise<boolean>;
  fetchLeadershipPositions: () => Promise<void>;
  addLeadershipPosition: (data: Partial<LeadershipPosition>) => Promise<boolean>;
  fetchLeadershipHierarchy: () => Promise<void>;
  fetchLeadershipTree: () => Promise<void>;
  assignPosition: (data: CreateLeadershipAssignment) => Promise<boolean>;
  updateAssignment: (id: number, data: Partial<CreateLeadershipAssignment>) => Promise<boolean>;
  removeAssignment: (id: number) => Promise<boolean>;
  fetchStats: () => Promise<void>;
  fetchAvailableEmployees: () => Promise<void>;
  fetchWorkingHoursSettings: () => Promise<void>;
  saveWorkingHoursSettings: (data: Partial<WorkingHoursSettings>) => Promise<boolean>;
  fetchNotificationStatus: () => Promise<void>;
  triggerNotifications: () => Promise<boolean>;
  fetchTeams: () => Promise<void>;
  fetchTeamById: (id: number) => Promise<void>;
  addTeam: (data: CreateTeam) => Promise<boolean>;
  editTeam: (id: number, data: Partial<CreateTeam>) => Promise<boolean>;
  removeTeam: (id: number) => Promise<boolean>;
  fetchAvailableManagers: () => Promise<void>;
  fetchAllEmployeesForManager: () => Promise<void>;
  fetchAllData: () => Promise<void>;
  resetMessages: () => void;
  resetError: () => void;
  resetStore: () => void;
}

const useOrganizationSetupStore = create<OrganizationSetupStore>((set, get) => ({
  // Initial state
  organizationDetails: null,
  leadershipPositions: [],
  leadershipHierarchy: [],
  leadershipTree: [],
  stats: null,
  availableEmployees: [],
  workingHoursSettings: null,
  notificationStatus: null,
  teams: [],
  selectedTeam: null,
  availableManagers: [],
  allEmployeesForManager: [],
  teamsLoading: false,
  loading: false,
  detailsLoading: false,
  hierarchyLoading: false,
  workingHoursLoading: false,
  triggerLoading: false,
  error: null,
  successMessage: null,

  // Fetch organization details
  fetchOrganizationDetails: async () => {
    try {
      set({ detailsLoading: true, error: null });
      const response = await getOrganizationDetails();

      if (response.success) {
        set({ organizationDetails: response.data, detailsLoading: false });
      } else {
        const errorMsg = typeof response.error === 'string'
          ? response.error
          : response.error?.message || 'Failed to fetch organization details';
        set({ error: errorMsg, detailsLoading: false });
      }
    } catch (error: any) {
      console.error("Fetch Organization Details Error:", error);
      set({ error: error.message || 'An error occurred', detailsLoading: false });
    }
  },

  // Save organization details
  saveOrganizationDetails: async (data) => {
    try {
      set({ loading: true, error: null, successMessage: null });
      const response = await updateOrganizationDetails(data);

      if (response.success) {
        set({
          organizationDetails: response.data,
          loading: false,
          successMessage: 'Organization details saved successfully'
        });
        // Refresh stats as years_in_business may have changed
        get().fetchStats();
        return true;
      } else {
        // Ensure error is always a string
        const errorMessage = typeof response.error === 'string'
          ? response.error
          : response.error?.message || 'Failed to save organization details';
        set({
          error: errorMessage,
          loading: false
        });
        return false;
      }
    } catch (error: any) {
      console.error("Save Organization Details Error:", error);
      const errorMessage = typeof error === 'string' ? error : (error?.message || 'An unexpected error occurred');
      set({ error: errorMessage, loading: false });
      return false;
    }
  },

  // Fetch leadership positions
  fetchLeadershipPositions: async () => {
    try {
      const response = await getLeadershipPositions();

      if (response.success) {
        set({ leadershipPositions: response.data });
      }
    } catch (error: any) {
      console.error("Fetch Leadership Positions Error:", error);
    }
  },

  // Add leadership position
  addLeadershipPosition: async (data) => {
    try {
      set({ loading: true, error: null, successMessage: null });
      const response = await createLeadershipPosition(data);

      if (response.success) {
        await get().fetchLeadershipPositions();
        set({ loading: false, successMessage: 'Position created successfully' });
        return true;
      }
      set({ error: response.error?.message, loading: false });
      return false;
    } catch (error: any) {
      console.error("Add Leadership Position Error:", error);
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Fetch leadership hierarchy (flat list)
  fetchLeadershipHierarchy: async () => {
    try {
      set({ hierarchyLoading: true, error: null });
      const response = await getLeadershipHierarchy();

      if (response.success) {
        set({ leadershipHierarchy: response.data, hierarchyLoading: false });
      } else {
        set({ error: response.error?.message, hierarchyLoading: false });
      }
    } catch (error: any) {
      console.error("Fetch Leadership Hierarchy Error:", error);
      set({ error: error.message, hierarchyLoading: false });
    }
  },

  // Fetch leadership tree
  fetchLeadershipTree: async () => {
    try {
      set({ hierarchyLoading: true, error: null });
      const response = await getLeadershipTree();

      if (response.success) {
        set({ leadershipTree: response.data, hierarchyLoading: false });
      } else {
        set({ error: response.error?.message, hierarchyLoading: false });
      }
    } catch (error: any) {
      console.error("Fetch Leadership Tree Error:", error);
      set({ error: error.message, hierarchyLoading: false });
    }
  },

  // Assign position
  assignPosition: async (data) => {
    try {
      set({ loading: true, error: null, successMessage: null });
      const response = await assignLeadershipPosition(data);

      if (response.success) {
        await Promise.all([
          get().fetchLeadershipHierarchy(),
          get().fetchLeadershipTree(),
          get().fetchStats()
        ]);
        set({ loading: false, successMessage: 'Position assigned successfully' });
        return true;
      }
      set({ error: response.error?.message, loading: false });
      return false;
    } catch (error: any) {
      console.error("Assign Position Error:", error);
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Update assignment
  updateAssignment: async (id, data) => {
    try {
      set({ loading: true, error: null, successMessage: null });
      const response = await updateLeadershipAssignment(id, data);

      if (response.success) {
        await Promise.all([
          get().fetchLeadershipHierarchy(),
          get().fetchLeadershipTree()
        ]);
        set({ loading: false, successMessage: 'Assignment updated successfully' });
        return true;
      }
      set({ error: response.error?.message, loading: false });
      return false;
    } catch (error: any) {
      console.error("Update Assignment Error:", error);
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Remove assignment
  removeAssignment: async (id) => {
    try {
      set({ loading: true, error: null, successMessage: null });
      const response = await removeLeadershipAssignment(id);

      if (response.success) {
        // Update local state optimistically
        const hierarchy = get().leadershipHierarchy.filter(item => item.id !== id);
        set({ leadershipHierarchy: hierarchy });

        await Promise.all([
          get().fetchLeadershipTree(),
          get().fetchStats()
        ]);
        set({ loading: false, successMessage: 'Assignment removed successfully' });
        return true;
      }
      set({ error: response.error?.message, loading: false });
      return false;
    } catch (error: any) {
      console.error("Remove Assignment Error:", error);
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Fetch stats
  fetchStats: async () => {
    try {
      const response = await getOrganizationStats();

      if (response.success) {
        set({ stats: response.data });
      }
    } catch (error: any) {
      console.error("Fetch Stats Error:", error);
    }
  },

  // Fetch available employees
  fetchAvailableEmployees: async () => {
    try {
      const response = await getAvailableEmployees();

      if (response.success) {
        set({ availableEmployees: response.data });
      }
    } catch (error: any) {
      console.error("Fetch Available Employees Error:", error);
    }
  },

  // Fetch working hours settings
  fetchWorkingHoursSettings: async () => {
    try {
      set({ workingHoursLoading: true, error: null });
      const response = await getWorkingHoursSettings();

      if (response.success) {
        set({ workingHoursSettings: response.data, workingHoursLoading: false });
      } else {
        const errorMsg = typeof response.error === 'string'
          ? response.error
          : response.error?.message || 'Failed to fetch working hours settings';
        set({ error: errorMsg, workingHoursLoading: false });
      }
    } catch (error: any) {
      console.error("Fetch Working Hours Settings Error:", error);
      set({ error: error.message || 'An error occurred', workingHoursLoading: false });
    }
  },

  // Save working hours settings
  saveWorkingHoursSettings: async (data) => {
    try {
      set({ loading: true, error: null, successMessage: null });
      const response = await updateWorkingHoursSettings(data);

      if (response.success) {
        set({
          workingHoursSettings: response.data,
          loading: false,
          successMessage: 'Working hours settings saved successfully'
        });
        return true;
      } else {
        const errorMessage = typeof response.error === 'string'
          ? response.error
          : response.error?.message || 'Failed to save working hours settings';
        set({
          error: errorMessage,
          loading: false
        });
        return false;
      }
    } catch (error: any) {
      console.error("Save Working Hours Settings Error:", error);
      const errorMessage = typeof error === 'string' ? error : (error?.message || 'An unexpected error occurred');
      set({ error: errorMessage, loading: false });
      return false;
    }
  },

  // Fetch notification status
  fetchNotificationStatus: async () => {
    try {
      const response = await getNotificationStatus();

      if (response.success) {
        set({ notificationStatus: response.data });
      }
    } catch (error: any) {
      console.error("Fetch Notification Status Error:", error);
    }
  },

  // Trigger notifications manually
  triggerNotifications: async () => {
    try {
      set({ triggerLoading: true, error: null, successMessage: null });
      const response = await triggerAttendanceNotifications();

      if (response.success) {
        set({
          notificationStatus: response.data,
          triggerLoading: false,
          successMessage: 'Attendance notifications triggered successfully. Check server logs for details.'
        });
        return true;
      } else {
        const errorMessage = typeof response.error === 'string'
          ? response.error
          : response.error?.message || 'Failed to trigger notifications';
        set({
          error: errorMessage,
          triggerLoading: false
        });
        return false;
      }
    } catch (error: any) {
      console.error("Trigger Notifications Error:", error);
      const errorMessage = typeof error === 'string' ? error : (error?.message || 'An unexpected error occurred');
      set({ error: errorMessage, triggerLoading: false });
      return false;
    }
  },

  // Fetch all teams
  fetchTeams: async () => {
    try {
      set({ teamsLoading: true, error: null });
      const response = await getAllTeams();

      if (response.success) {
        set({ teams: response.data, teamsLoading: false });
      } else {
        const errorMsg = typeof response.error === 'string'
          ? response.error
          : response.error?.message || 'Failed to fetch teams';
        set({ error: errorMsg, teamsLoading: false });
      }
    } catch (error: any) {
      console.error("Fetch Teams Error:", error);
      set({ error: error.message || 'An error occurred', teamsLoading: false });
    }
  },

  // Fetch single team by ID
  fetchTeamById: async (id) => {
    try {
      set({ teamsLoading: true, error: null });
      const response = await getTeamById(id);

      if (response.success) {
        set({ selectedTeam: response.data, teamsLoading: false });
      } else {
        set({ error: response.error?.message, teamsLoading: false });
      }
    } catch (error: any) {
      console.error("Fetch Team By ID Error:", error);
      set({ error: error.message, teamsLoading: false });
    }
  },

  // Add new team
  addTeam: async (data) => {
    try {
      set({ loading: true, error: null, successMessage: null });
      const response = await createTeam(data);

      if (response.success) {
        await get().fetchTeams();
        await get().fetchStats();
        set({ loading: false, successMessage: 'Team created successfully' });
        return true;
      }
      set({ error: response.error?.message, loading: false });
      return false;
    } catch (error: any) {
      console.error("Add Team Error:", error);
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Edit team
  editTeam: async (id, data) => {
    try {
      set({ loading: true, error: null, successMessage: null });
      const response = await updateTeam(id, data);

      if (response.success) {
        await get().fetchTeams();
        set({ loading: false, successMessage: 'Team updated successfully' });
        return true;
      }
      set({ error: response.error?.message, loading: false });
      return false;
    } catch (error: any) {
      console.error("Edit Team Error:", error);
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Remove team
  removeTeam: async (id) => {
    try {
      set({ loading: true, error: null, successMessage: null });
      const response = await deleteTeam(id);

      if (response.success) {
        const teams = get().teams.filter(t => t.id !== id);
        set({ teams, loading: false, successMessage: 'Team deleted successfully' });
        await get().fetchStats();
        return true;
      }
      set({ error: response.error?.message, loading: false });
      return false;
    } catch (error: any) {
      console.error("Remove Team Error:", error);
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Fetch available managers
  fetchAvailableManagers: async () => {
    try {
      const response = await getAvailableManagers();

      if (response.success) {
        set({ availableManagers: response.data });
      }
    } catch (error: any) {
      console.error("Fetch Available Managers Error:", error);
    }
  },

  // Fetch all employees for manager dropdown
  fetchAllEmployeesForManager: async () => {
    try {
      const response = await getAllEmployeesForManager();

      if (response.success) {
        set({ allEmployeesForManager: response.data });
      }
    } catch (error: any) {
      console.error("Fetch All Employees For Manager Error:", error);
    }
  },

  // Fetch all data at once
  fetchAllData: async () => {
    try {
      set({ loading: true, error: null });

      await Promise.all([
        get().fetchOrganizationDetails(),
        get().fetchLeadershipPositions(),
        get().fetchLeadershipHierarchy(),
        get().fetchLeadershipTree(),
        get().fetchStats(),
        get().fetchWorkingHoursSettings(),
        get().fetchTeams()
      ]);

      set({ loading: false });
    } catch (error: any) {
      console.error("Fetch All Data Error:", error);
      set({ error: error.message, loading: false });
    }
  },

  // Reset messages
  resetMessages: () => set({ error: null, successMessage: null }),

  // Reset error
  resetError: () => set({ error: null }),

  // Reset store to initial state
  resetStore: () => set({
    organizationDetails: null,
    leadershipPositions: [],
    leadershipHierarchy: [],
    leadershipTree: [],
    stats: null,
    availableEmployees: [],
    workingHoursSettings: null,
    notificationStatus: null,
    teams: [],
    selectedTeam: null,
    availableManagers: [],
    allEmployeesForManager: [],
    teamsLoading: false,
    loading: false,
    detailsLoading: false,
    hierarchyLoading: false,
    workingHoursLoading: false,
    triggerLoading: false,
    error: null,
    successMessage: null
  })
}));

// Register this store for centralized reset
registerStoreReset('organizationSetup', () => useOrganizationSetupStore.getState().resetStore());

export default useOrganizationSetupStore;
